import { useState } from 'react';
import { Card, Table, Tag, Button, Space, Alert, Popconfirm, EmptyState, Tooltip, Typography, message } from '../../ui';
import type { TagTone } from '../../ui';
import { ShieldCheck, UserCog, Plus, Ban } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { GrantCoverageModal } from './GrantCoverageModal';

const STATUS_COLOR: Record<string, TagTone> = { LEAVE: 'blue', ABSENT: 'red' };
const fmt = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');

/** Coverage management: who's absent without cover (the attendance prompt), plus the
 *  active/upcoming coverage grants — all scoped server-side to the caller's team. */
export function CoverageManagerCard() {
  const qc = useQueryClient();
  const [grantOpen, setGrantOpen] = useState(false);
  const [presetAbsentId, setPresetAbsentId] = useState<string | undefined>();

  const { data: needed } = useQuery({
    queryKey: ['coverage-needed'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/coverage/needed' }),
  });
  const { data: grants, isLoading } = useQuery({
    queryKey: ['territory-coverage-grants'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/coverage' }),
  });

  const endCoverage = useMutation({
    mutationFn: (g: any) => request({ url: '/territory-assignments/coverage/end', method: 'POST', data: { covererUserId: String(g.covererId), absentUserId: String(g.absentId), validFrom: g.validFrom ?? undefined, validTo: g.validTo ?? undefined } }),
    onSuccess: () => {
      message.success('Coverage ended');
      qc.invalidateQueries({ queryKey: ['territory-coverage-grants'] });
      qc.invalidateQueries({ queryKey: ['coverage-needed'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to end coverage'),
  });

  const openGrant = (absentId?: string) => { setPresetAbsentId(absentId); setGrantOpen(true); };

  return (
    <Card
      title={<Space><ShieldCheck size={16} />Coverage</Space>}
      extra={<Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => openGrant(undefined)}>Grant coverage</Button>}
      style={{ marginBottom: 14 }}
    >
      {(needed ?? []).length > 0 && (
        <Alert
          type="warning"
          style={{ marginBottom: 12 }}
          title={`${needed!.length} sales person(s) away today without cover`}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {needed!.map((n: any) => (
                <div key={n.userId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>
                    <b>{n.fullName}</b>{' '}
                    <Tag tone={STATUS_COLOR[n.status] ?? 'neutral'}>{n.status}</Tag>
                    <Typography.Text type="secondary">{n.territories} territor{Number(n.territories) === 1 ? 'y' : 'ies'}</Typography.Text>
                  </span>
                  <Button size="sm" icon={<UserCog size={16} />} onClick={() => openGrant(String(n.userId))}>Assign cover</Button>
                </div>
              ))}
            </Space>
          }
        />
      )}

      {/* An empty grants table drew a half-screen-tall "No active coverage" box directly under the tab
          badge, so the badge's number appeared to be describing THAT emptiness. Nothing pending is a
          one-line fact — say it in one line and let the work above stay at the top of the page. */}
      {!isLoading && !(grants ?? []).length ? (
        <p className="m-0 text-sm text-muted">
          Nobody is standing in for an absent sales person right now. Use Grant coverage when
          someone is away and their territories need a temporary owner.
        </p>
      ) : (
      <Table
        rowKey={(g) => `${g.covererId}-${g.absentId}-${g.validFrom}`}
        size="small"
        loading={isLoading}
        dataSource={grants ?? []}
        pagination={false}
        locale={{ emptyText: <EmptyState title="No active coverage" /> }}
        columns={[
          { title: 'Coverer', dataIndex: 'covererName', render: (v) => <b>{v}</b> },
          { title: 'Covering for', dataIndex: 'absentName', render: (v) => v ?? '—' },
          { title: 'From', dataIndex: 'validFrom', render: fmt },
          { title: 'To', dataIndex: 'validTo', render: fmt },
          { title: 'Territories', dataIndex: 'territories', align: 'right' as const },
          {
            title: 'Status', dataIndex: 'active',
            render: (v: any) => (Number(v) === 1 ? <Tag tone="green">Active</Tag> : <Tag tone="gold">Upcoming</Tag>),
          },
          {
            title: '', key: 'actions', align: 'right' as const,
            render: (_: any, g: any) => (
              <Tooltip label="End this coverage">
                <Popconfirm title={`End ${g.covererName}'s coverage?`} okText="End" okButtonProps={{ danger: true }} onConfirm={() => endCoverage.mutate(g)}>
                  <Button size="sm" variant="text" aria-label="End this coverage" icon={<Ban size={16} className="text-danger-600 dark:text-danger-500" />} />
                </Popconfirm>
              </Tooltip>
            ),
          },
        ]}
      />
      )}

      <GrantCoverageModal open={grantOpen} presetAbsentId={presetAbsentId} onClose={() => setGrantOpen(false)} />
    </Card>
  );
}
